/**
 * DI-3-S61 §2.2 — the History modal's empty / loading / error states in the
 * IntakeNotice idiom. COPIED RECIPE of
 * `apps/web/src/features/intake/wizard/IntakeNotice.tsx` (DI-3-S41, approved
 * 2026-10-23): paper box, divider border, 4 px coloured left edge, icon, ONE
 * sentence (bold name · muted state), actions right at the compact 32 px
 * height. `apps/lyse` imports nothing from `apps/web`, so the recipe is
 * copied, not imported.
 *
 * Also the DI-3-C `SlotChip` recipe (`…/intake/decide/SlotChip.tsx`): D91's
 * `statusChipStyle` (hue owns fill, dot and label; no keyline), compact.
 */
import type { ReactElement, ReactNode } from "react";
import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import {
  compactFilledCtaSx,
  radius,
  spacing,
  statusChipStyle,
  type StatusSlot,
} from "@aeriosiq/ui";

export type NoticeTone = "info" | "warning" | "error";

const TONE_COLOR: Record<NoticeTone, string> = {
  info: "info.main",
  warning: "warning.main",
  error: "error.main",
};

export function HistoryNotice({
  tone,
  icon,
  children,
  actions,
  testId,
}: {
  tone: NoticeTone;
  icon: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  testId?: string;
}): ReactElement {
  return (
    <Box
      role="note"
      data-testid={testId}
      data-tone={tone}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        border: 1,
        borderColor: "divider",
        borderLeft: 4,
        borderLeftColor: TONE_COLOR[tone],
        borderRadius: `${String(radius.card)}px`,
        pl: 1.75,
        pr: 1.25,
        py: 1,
        minHeight: (t) => t.spacing(6.5), // 52 px — the IntakeNotice recipe
        bgcolor: "background.paper",
      }}
    >
      <Box
        aria-hidden
        sx={{ display: "flex", flex: "none", color: TONE_COLOR[tone] }}
      >
        {icon}
      </Box>
      <Typography variant="body2" component="div" sx={{ flex: 1, minWidth: 0 }}>
        {children}
      </Typography>
      {actions && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flex: "none", "& .MuiButton-root": compactFilledCtaSx }}
        >
          {actions}
        </Stack>
      )}
    </Box>
  );
}

/** The muted state part of a notice's sentence. */
export function NoticeMuted({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  return (
    <Box component="span" sx={{ color: "text.secondary" }}>
      {children}
    </Box>
  );
}

/** A D91 slot chip with a dot (the DI-3-S25 coverage chip's look). */
export function HistoryChip({
  slot,
  label,
  testId,
}: {
  slot: StatusSlot;
  label: string;
  testId?: string;
}): ReactElement {
  const theme = useTheme();
  const style = statusChipStyle(theme, slot);
  return (
    <Chip
      size="small"
      label={label}
      data-slot={slot}
      {...(testId ? { "data-testid": testId } : {})}
      icon={
        <Box
          component="span"
          aria-hidden
          sx={{
            width: spacing.sm,
            height: spacing.sm,
            borderRadius: `${String(radius.pill)}px`,
            backgroundColor: style.dotColor,
            flexShrink: 0,
          }}
        />
      }
      sx={{
        backgroundColor: style.backgroundColor,
        color: style.color,
        flexShrink: 0,
        maxWidth: "100%",
        "& .MuiChip-icon": { ml: 1, mr: -0.25 },
      }}
    />
  );
}
